import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/context/AuthContext";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

const STATUSES = [
  { key: "new", label: "New", color: "#FF3B30" },
  { key: "contacted", label: "Contacted", color: "#FFB224" },
  { key: "closed", label: "Closed", color: "#9A9A9A" },
];

export const EnquiriesInbox = () => {
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch (e) {
      setError("Could not load enquiries. Check your connection and retry.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    const prev = enquiries;
    setEnquiries((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await api.patch(`/enquiries/${id}/status`, { status });
    } catch (e) {
      setEnquiries(prev);
      toast.error("Could not update status. Try again.");
    }
  };

  const countFor = (key) =>
    enquiries ? enquiries.filter((q) => key === "all" || (q.status || "new") === key).length : 0;

  const visible = enquiries
    ? enquiries.filter((q) => filter === "all" || (q.status || "new") === filter)
    : [];

  return (
    <div data-testid="enquiries-inbox">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display font-bold uppercase tracking-tight text-white text-3xl md:text-4xl">
          Enquiries{" "}
          <span className="text-[#FF3B30] font-mono-plex text-lg align-top" data-testid="enquiries-count">
            {enquiries ? `[${enquiries.length}]` : ""}
          </span>
        </h2>
        <button
          data-testid="enquiries-refresh-btn"
          onClick={load}
          className="border border-[#222222] hover:border-[#FF3B30] text-white p-3 transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {enquiries && (
        <div className="flex flex-wrap gap-px bg-[#222222] border border-[#222222] w-fit mb-8">
          {[{ key: "all", label: "All" }, ...STATUSES].map((s) => (
            <button
              key={s.key}
              data-testid={`enquiries-filter-${s.key}`}
              onClick={() => setFilter(s.key)}
              className={`px-4 py-2 font-mono-plex text-xs uppercase tracking-[0.15em] font-bold transition-colors ${
                filter === s.key ? "bg-white text-black" : "bg-[#0A0A0A] text-[#9A9A9A] hover:text-white"
              }`}
            >
              {s.label} [{countFor(s.key)}]
            </button>
          ))}
        </div>
      )}

      {error ? (
        <p data-testid="enquiries-error" className="font-mono-plex text-[#FF3B30] text-sm">
          {error}
        </p>
      ) : enquiries === null ? (
        <p className="font-mono-plex text-[#9A9A9A] text-sm">Loading…</p>
      ) : visible.length === 0 ? (
        <p data-testid="enquiries-empty" className="font-mono-plex text-[#9A9A9A] text-sm">
          {filter === "all" ? "No enquiries yet." : `No ${filter} enquiries.`}
        </p>
      ) : (
        <div className="space-y-px bg-[#222222] border border-[#222222]">
          {visible.map((q) => {
            const st = (q.status || "new");
            const stMeta = STATUSES.find((s) => s.key === st) || STATUSES[0];
            return (
              <div key={q.id} data-testid={`enquiry-row-${q.id}`} className="bg-[#0A0A0A] p-6 md:p-8">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-display font-bold uppercase text-white text-xl flex items-center gap-3">
                    <span
                      data-testid={`enquiry-status-badge-${q.id}`}
                      className="inline-block w-2 h-2"
                      style={{ backgroundColor: stMeta.color }}
                      title={stMeta.label}
                    />
                    {q.name}
                  </span>
                  <span className="font-mono-plex text-[#9A9A9A] text-xs">{fmtDate(q.created_at)}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 font-mono-plex text-sm">
                  <span className="text-white flex items-center gap-3">
                    {q.phone}
                    <a href={`tel:${q.phone}`} className="text-[#FF3B30] hover:text-white transition-colors" title="Call">
                      <Phone className="w-4 h-4" />
                    </a>
                    <a
                      href={`https://wa.me/${q.phone.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#FF3B30] hover:text-white transition-colors"
                      title="WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  </span>
                  {q.brand && <span className="text-[#AAAAAA]">BRAND: {q.brand}</span>}
                  {q.product && <span className="text-[#AAAAAA]">PRODUCT: {q.product}</span>}
                </div>
                {q.message && <p className="mt-4 text-[#CCCCCC] font-mono-plex text-sm leading-relaxed">{q.message}</p>}
                <div className="mt-5 flex gap-px bg-[#222222] border border-[#222222] w-fit">
                  {STATUSES.map((s) => (
                    <button
                      key={s.key}
                      data-testid={`enquiry-status-${s.key}-${q.id}`}
                      onClick={() => st !== s.key && setStatus(q.id, s.key)}
                      className={`px-3 py-1.5 font-mono-plex text-[10px] uppercase tracking-[0.15em] font-bold transition-colors ${
                        st === s.key ? "text-black" : "bg-[#0A0A0A] text-[#9A9A9A] hover:text-white"
                      }`}
                      style={st === s.key ? { backgroundColor: s.color } : undefined}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
